import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import Linkify from 'react-linkify';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import Title from '../../commonComponents/Title';
import translate from '../../commonComponents/translate';
import ContentContainer from '../../commonComponents/pageLayout/ContentContainer';
import SessionProp from './propTypes/sessionProp';
import CountryProp from '../../commonComponents/forms/Address/CountryProp';
import ListTrainer from './list/listItem/ListTrainer';
import { compose } from '../../services/utilityFunctions';
import withCSRF from '../../commonComponents/withCSRF';
import Notifications from '../../services/notifications';
import { useModal, useSanitize } from '../../services/customHooks';
import LoginModal from '../signIn/modals/LoginModal';
import TenantProp from '../../commonComponents/propTypes/tenantProp';
import SessionItem from './SessionItem';
import SubHeader from '../../commonComponents/pageLayout/SubHeader';
import EnrollForm from '../../commonComponents/forms/EnrollForm';
import CurrencyProp from '../profile/propTypes/currencyProp';

function SessionDetailsPublic(props) {
  const {
    t,
    _csrf,
    session,
    languages,
    organizationIdentifier,
    tenant,
    availableCurrencies,
    availableCountries,
  } = props;

  const countries = availableCountries.map(country => ({
    id: country.code,
    text: country.name,
  }));
  const currencies = availableCurrencies.map(currency => ({
    id: currency.code,
    text: currency.name,
  }));
  const isPublic = session.mode === 'public';
  const description = useSanitize(session.description);
  const [showModal, setShowModal] = useModal({ show: false, data: null });
  const handleModal = useCallback(show => {
    setShowModal({ show, data: null });
  }, []);

  const browserHistory = useHistory();

  const handleSubscribe = () => {
    if (isPublic) {
      axios
        .put(
          `/api/access-session/${session._id}/subscribe`,
          {},
          {
            headers: {
              'Accept-Version': '1.0.0',
              'X-Gamification-API': 'Games',
              'X-CSRF-Token': _csrf,
              _csrf,
            },
          },
        )
        .then(() => {
          Notifications.showNotificationSuccess(
            t('success'),
            t('success_subscribe_access_session'),
          );
          window.location.href = `/academy/sessions/${session.slug}${
            session.price > 0 || session.promotionsPrice > 0
              ? '?enroll=true'
              : ''
          }`;
        })
        .catch(err => {
          Notifications.showErrorFromRequest(err);
          console.log('Err', err);
        });
    } else {
      window.location.href = `/academy/sessions/${session.slug}`;
    }
  };

  return (
    <>
      {showModal.show && (
        <LoginModal
          onClose={() => handleModal(false)}
          languages={[]}
          showRegisterAccount={false}
          logo={null}
          redirectLink={browserHistory.location.pathname}
          onLoggedIn={() => handleSubscribe(true)}
          ignoreRedirection
        />
      )}
      <Title title={t('sessions_head_title')}>
        <ContentContainer>
          <div className="sessionIsPublicSubheader">
            <SubHeader
              title={t('academy.session.detail')}
              breadcrumbs={[
                { link: '/academy/sessions', text: t('header_academy') },
                { link: '', text: session.name },
              ]}
            />
          </div>
          <div id="sessionPublic">
            <div className="m-content m-portlet session-public-full-container">
              <div className="full-page-container-with-subheader session-public-content-container">
                <SessionItem
                  key={session._id}
                  t={t}
                  session={session}
                  languages={languages}
                  mySessionsMode={false}
                  isHostTrainerVisible
                  showPromotion
                  isPublicPage
                />
                <div className="session-public-details-container">
                  <div className="m-content m-portlet session-public-about-container">
                    <div className="session-public-about-content">
                      {(description || session.summary) && (
                        <div className="description">
                          <h4>{t('sessionDetails_about')}</h4>
                          {description ? (
                            <div
                              className="description-value"
                              dangerouslySetInnerHTML={{
                                __html: description,
                              }}
                            />
                          ) : (
                            <Linkify>{session.summary}</Linkify>
                          )}
                        </div>
                      )}
                      {session?.trainer && (
                        <div className="trainer-wrapper">
                          <hr />
                          <div
                            className="trainer-container"
                            style={{ width: '100%' }}
                          >
                            <h5>{t('sessionDetails_trainer')}</h5>
                            <div className="sessionDetail-container-body">
                              <ListTrainer user={session.trainer} t={t} />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <EnrollForm
                    languages={languages}
                    isPublic={session.mode === 'public'}
                    onComplete={handleSubscribe}
                    onLoginModalOpen={handleModal}
                    tenant={tenant}
                    organizationIdentifier={organizationIdentifier}
                    title={t('academy.session.public.enrol_session')}
                    buttonTxt={t('login_and_subscribe')}
                    loginTxt={t(
                      'academy.session.public.signup_subscribe_button',
                    )}
                    countries={countries}
                    currencies={currencies}
                    isFromSession
                  />
                </div>
              </div>
            </div>
          </div>
        </ContentContainer>
      </Title>
    </>
  );
}

SessionDetailsPublic.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  tenant: PropTypes.shape(TenantProp.propType).isRequired,
  session: PropTypes.shape(SessionProp.propType).isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  organizationIdentifier: PropTypes.string.isRequired,
  availableCountries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  availableCurrencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired,
};

SessionDetailsPublic.defaultProps = {};

export default compose(translate, withCSRF)(SessionDetailsPublic);
